/**
 * Advanced Conversion Tracking Module
 * Handles comprehensive marketing data collection and conversion tracking
 */

export class AdvancedTracking {
  constructor() {
    this.sessionData = this.initializeSessionData();
    this.userJourney = [];
    this.behavioralData = {};
    this.deviceData = {};
    this.marketingData = {};

    this.init();
  }

  /**
   * Initialize tracking system
   */
  init() {
    this.collectMarketingData();
    this.collectDeviceData();
    this.trackUserJourney();
    this.trackBehavioralData();
    this.setupFormTracking();
    this.setupPageTracking();

    // Initialize GTM tracking
    this.initializeGTMTracking();
  }

  /**
   * Initialize GTM tracking
   */
  initializeGTMTracking() {
    // Push initial data to GTM
    this.pushToDataLayer('tracking_initialized', {
      session_id: this.sessionData.session_id,
      marketing_data: this.marketingData,
      device_data: this.deviceData,
      page_title: document.title,
      page_location: window.location.href,
      timestamp: new Date().toISOString(),
    });

    // Track session start
    this.trackUserEngagementForGTM('session_start', {
      session_id: this.sessionData.session_id,
      return_visitor: this.sessionData.return_visitor,
      first_touch_data: this.sessionData.first_touch_data,
    });
  }

  /**
   * Initialize session data
   */
  initializeSessionData() {
    if (!sessionStorage.getItem('tracking_session')) {
      const sessionData = {
        session_id: this.generateSessionId(),
        start_time: Date.now(),
        page_views: 0,
        previous_pages: [],
        form_interactions: [],
        return_visitor: this.isReturnVisitor(),
        first_touch_data: this.getFirstTouchData(),
      };

      sessionStorage.setItem('tracking_session', JSON.stringify(sessionData));
    }

    return JSON.parse(sessionStorage.getItem('tracking_session'));
  }

  /**
   * Collect comprehensive marketing data
   */
  collectMarketingData() {
    const urlParams = new URLSearchParams(window.location.search);

    this.marketingData = {
      // UTM Parameters
      utm_source:
        urlParams.get('utm_source') || this.getFromStorage('utm_source'),
      utm_medium:
        urlParams.get('utm_medium') || this.getFromStorage('utm_medium'),
      utm_campaign:
        urlParams.get('utm_campaign') || this.getFromStorage('utm_campaign'),
      utm_term: urlParams.get('utm_term') || this.getFromStorage('utm_term'),
      utm_content:
        urlParams.get('utm_content') || this.getFromStorage('utm_content'),

      // Click IDs
      gclid: urlParams.get('gclid') || this.getFromStorage('gclid'),
      fbclid: urlParams.get('fbclid') || this.getFromStorage('fbclid'),
      msclkid: urlParams.get('msclkid') || this.getFromStorage('msclkid'),
      ttclid: urlParams.get('ttclid') || this.getFromStorage('ttclid'),

      // Custom parameters
      campaign_source:
        urlParams.get('campaign_source') ||
        this.getFromStorage('campaign_source'),
      source: urlParams.get('source') || this.getFromStorage('source'),
      ref: urlParams.get('ref') || this.getFromStorage('ref'),

      // Page data
      referrer: document.referrer,
      landing_page: window.location.href,
      page_title: document.title,

      // Session data
      session_id: this.sessionData.session_id,
      user_agent: navigator.userAgent,
      language: navigator.language,
      timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Store marketing data for future use
    this.storeMarketingData();
  }

  /**
   * Collect device and browser data
   */
  collectDeviceData() {
    this.deviceData = {
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      color_depth: screen.colorDepth,
      pixel_ratio: window.devicePixelRatio,
      user_agent: navigator.userAgent,
      browser: this.detectBrowser(),
      os: this.detectOS(),
      device_type: this.detectDeviceType(),
      browser_language: navigator.language,
      connection_type: this.detectConnectionType(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookies_enabled: navigator.cookieEnabled,
      java_enabled: navigator.javaEnabled ? navigator.javaEnabled() : false,
    };
  }

  /**
   * Track user journey
   */
  trackUserJourney() {
    const currentPage = {
      url: window.location.href,
      title: document.title,
      timestamp: Date.now(),
      referrer: document.referrer,
      time_on_page: 0,
    };

    // Update session data
    this.sessionData.page_views++;
    this.sessionData.previous_pages.push(currentPage);

    // Keep only last 10 pages
    if (this.sessionData.previous_pages.length > 10) {
      this.sessionData.previous_pages =
        this.sessionData.previous_pages.slice(-10);
    }

    // Store updated session data
    sessionStorage.setItem(
      'tracking_session',
      JSON.stringify(this.sessionData)
    );

    // Track page view
    this.trackPageView(currentPage);

    // Track page view for GTM
    this.trackPageViewForGTM();
  }

  /**
   * Track behavioral data
   */
  trackBehavioralData() {
    this.behavioralData = {
      scroll_depth: 0,
      mouse_movements: 0,
      clicks: 0,
      form_interactions: [],
      time_on_page: 0,
      page_views: this.sessionData.page_views,
      session_duration: Date.now() - this.sessionData.start_time,
      return_visitor: this.sessionData.return_visitor,
    };

    // Track scroll depth
    this.trackScrollDepth();

    // Track mouse movements
    this.trackMouseMovements();

    // Track clicks
    this.trackClicks();

    // Track form interactions
    this.trackFormInteractions();

    // Track time on page
    this.trackTimeOnPage();
  }

  /**
   * Setup form tracking
   */
  setupFormTracking() {
    const forms = document.querySelectorAll('form');

    forms.forEach(form => {
      // Track form start
      form.addEventListener('focusin', e => {
        if (
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.tagName === 'SELECT'
        ) {
          this.trackFormInteraction('form_start', form.id || 'unknown');
        }
      });

      // Track form submission
      form.addEventListener('submit', e => {
        e.preventDefault(); // Prevent default form submission
        this.trackFormSubmission(form);
      });

      // Track field interactions
      const fields = form.querySelectorAll('input, textarea, select');
      fields.forEach(field => {
        field.addEventListener('focus', () => {
          this.trackFormInteraction(
            'field_focus',
            field.name || field.id || 'unknown'
          );
        });

        field.addEventListener('blur', () => {
          this.trackFormInteraction(
            'field_blur',
            field.name || field.id || 'unknown'
          );
        });

        field.addEventListener('change', () => {
          this.trackFormInteraction(
            'field_change',
            field.name || field.id || 'unknown'
          );
        });
      });
    });
  }

  /**
   * Setup page tracking
   */
  setupPageTracking() {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackPageInteraction('page_hidden');
      } else {
        this.trackPageInteraction('page_visible');
      }
    });

    // Track before unload
    window.addEventListener('beforeunload', () => {
      this.trackPageInteraction('page_unload');
      this.sendBehavioralData();
    });

    // Track page focus/blur
    window.addEventListener('focus', () =>
      this.trackPageInteraction('window_focus')
    );
    window.addEventListener('blur', () =>
      this.trackPageInteraction('window_blur')
    );
  }

  /**
   * Track form submission with comprehensive data
   */
  trackFormSubmission(form) {
    const formData = new FormData(form);
    const formType = this.determineFormType(form);
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalBtnContent = submitBtn ? submitBtn.innerHTML : '';

    // Check privacy consent for contact forms
    if (formType === 'contact_form') {
      const consentCheckbox = document.getElementById('privacy-consent');
      if (consentCheckbox && !consentCheckbox.checked) {
        this.showToast(
          'error',
          'Please accept the Privacy Policy and consent to data processing'
        );
        return;
      }
    }

    // Show loading state
    if (submitBtn) {
      this.setFormLoadingState(true, submitBtn);
    }

    // Add comprehensive tracking data
    this.addTrackingDataToForm(formData);

    // Determine submission endpoint
    const endpoint = this.getFormEndpoint(formType);

    // Submit form with tracking data and analytics
    this.submitFormWithAnalytics(
      form,
      formData,
      formType,
      submitBtn,
      originalBtnContent,
      endpoint
    );
  }

  /**
   * Add comprehensive tracking data to form
   */
  addTrackingDataToForm(formData) {
    // Marketing data
    Object.entries(this.marketingData).forEach(([key, value]) => {
      if (value) {
        formData.append(key, value);
      }
    });

    // Device data
    Object.entries(this.deviceData).forEach(([key, value]) => {
      if (value) {
        formData.append(`device_${key}`, value);
      }
    });

    // Behavioral data
    Object.entries(this.behavioralData).forEach(([key, value]) => {
      if (value) {
        formData.append(
          `behavior_${key}`,
          typeof value === 'object' ? JSON.stringify(value) : value
        );
      }
    });

    // User journey
    formData.append('user_journey', JSON.stringify(this.userJourney));

    // Session data
    formData.append('session_data', JSON.stringify(this.sessionData));

    // Form completion time
    formData.append('form_completion_time', this.calculateFormCompletionTime());

    // Current timestamp
    formData.append('submission_timestamp', new Date().toISOString());
  }

  /**
   * Track conversion (now handled in sendToAnalytics)
   */
  trackConversion(formType, formData) {
    // This method is now integrated into sendToAnalytics
    // to prevent duplicate event sending
    console.log('Conversion tracking integrated into sendToAnalytics');
  }

  /**
   * Send to analytics platforms
   */
  sendToAnalytics(formType, formData) {
    // Google Tag Manager DataLayer - Use specific event names
    const eventName = `${formType}_submit`;
    this.pushToDataLayer(eventName, {
      event_category: 'engagement',
      event_label: formType,
      value: this.calculateConversionValue(formType),
      currency: 'EUR',
      lead_score: this.calculateLeadScore(formData),
      form_type: formType,
      marketing_source: this.marketingData.utm_source,
      utm_campaign: this.marketingData.utm_campaign,
      utm_medium: this.marketingData.utm_medium,
      utm_term: this.marketingData.utm_term,
      utm_content: this.marketingData.utm_content,
      gclid: this.marketingData.gclid,
      fbclid: this.marketingData.fbclid,
      device_type: this.deviceData.device_type,
      browser: this.deviceData.browser,
      os: this.deviceData.os,
      screen_resolution: this.deviceData.screen_resolution,
      scroll_depth: this.behavioralData.scroll_depth,
      time_on_page: this.behavioralData.time_on_page,
      session_duration: this.behavioralData.session_duration,
      return_visitor: this.behavioralData.return_visitor,
      page_views: this.behavioralData.page_views,
      user_journey: this.userJourney,
      session_data: this.sessionData,
    });

    // Also send conversion event
    this.sendConversionEvent(formType, formData);

    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', `${formType}_submit`, {
        event_category: 'engagement',
        event_label: formType,
        value: this.calculateConversionValue(formType),
        currency: 'EUR',
        custom_parameters: {
          lead_score: this.calculateLeadScore(formData),
          form_type: formType,
          marketing_source: this.marketingData.utm_source,
        },
      });
    }

    // Meta Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead', {
        content_category: 'Pool Construction',
        content_name: `${formType} Submission`,
        value: this.calculateConversionValue(formType),
        currency: 'EUR',
        custom_data: {
          form_type: formType,
          lead_score: this.calculateLeadScore(formData),
          marketing_source: this.marketingData.utm_source,
        },
      });
    }

    // LinkedIn Insight Tag
    if (typeof lintrk !== 'undefined') {
      lintrk('track', {
        conversion_id: 'LINKEDIN_CONVERSION_ID',
        value: this.calculateConversionValue(formType),
        currency: 'EUR',
      });
    }

    // TikTok Pixel
    if (typeof ttq !== 'undefined') {
      ttq.track('SubmitForm', {
        content_type: formType,
        content_category: 'Pool Construction',
        value: this.calculateConversionValue(formType),
        currency: 'EUR',
      });
    }
  }

  /**
   * Send conversion event to GTM
   */
  sendConversionEvent(formType, formData) {
    const conversionEventName = `${formType}_conversion`;
    this.pushToDataLayer(conversionEventName, {
      event_category: 'conversion',
      event_label: 'advanced_conversion',
      value: this.calculateConversionValue(formType),
      currency: 'EUR',
      lead_score: this.calculateLeadScore(formData),
      form_type: formType,
      conversion_type: this.determineConversionType(formType),
      conversion_value: this.calculateConversionValue(formType),
      marketing_data: this.marketingData,
      device_data: this.deviceData,
      behavioral_data: this.behavioralData,
      user_journey: this.userJourney,
      session_data: this.sessionData,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Send conversion to analytics (now handled by sendConversionEvent)
   */
  sendConversionToAnalytics(conversionResult, conversionData) {
    // This method is now integrated into sendConversionEvent
    // to prevent duplicate event sending
    console.log('Conversion analytics integrated into sendConversionEvent');
  }

  /**
   * Calculate lead score
   */
  calculateLeadScore(formData) {
    let score = 0;

    // Base score for form submission
    score += 20;

    // Form type scoring
    const formTypeScores = {
      contact_form: 30,
      quote_request: 40,
      newsletter: 10,
      callback_request: 25,
      service_inquiry: 35,
    };

    const formType = this.determineFormType(document.querySelector('form'));
    score += formTypeScores[formType] || 20;

    // Behavioral scoring
    if (this.behavioralData.scroll_depth > 75) score += 10;
    if (this.behavioralData.time_on_page > 120) score += 10;
    if (this.behavioralData.return_visitor) score += 15;

    // Marketing source scoring
    const sourceScores = {
      google_ads: 25,
      facebook_ads: 20,
      linkedin_ads: 30,
      organic_search: 15,
      direct: 10,
    };

    score += sourceScores[this.marketingData.utm_source] || 10;

    return Math.min(score, 100);
  }

  /**
   * Calculate conversion value
   */
  calculateConversionValue(formType) {
    const values = {
      contact_form: 50000,
      quote_request: 75000,
      newsletter: 1000,
      callback_request: 25000,
      service_inquiry: 30000,
    };

    return values[formType] || 10000;
  }

  /**
   * Determine form type
   */
  determineFormType(form) {
    if (!form) return 'contact_form';

    const formId = form.id || '';
    const formClass = form.className || '';

    if (formId.includes('quote') || formClass.includes('quote'))
      return 'quote_request';
    if (formId.includes('newsletter') || formClass.includes('newsletter'))
      return 'newsletter';
    if (formId.includes('callback') || formClass.includes('callback'))
      return 'callback_request';
    if (formId.includes('service') || formClass.includes('service'))
      return 'service_inquiry';

    return 'contact_form';
  }

  /**
   * Determine conversion type
   */
  determineConversionType(formType) {
    const types = {
      contact_form: 'lead',
      quote_request: 'quote',
      newsletter: 'newsletter',
      callback_request: 'callback',
      service_inquiry: 'lead',
    };

    return types[formType] || 'lead';
  }

  /**
   * Track scroll depth
   */
  trackScrollDepth() {
    let maxScroll = 0;

    window.addEventListener('scroll', () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / scrollHeight) * 100);

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        this.behavioralData.scroll_depth = maxScroll;

        // Track scroll depth for GTM
        this.trackScrollDepthForGTM(scrollPercent);
      }
    });
  }

  /**
   * Track mouse movements
   */
  trackMouseMovements() {
    let mouseMovements = 0;

    document.addEventListener('mousemove', () => {
      mouseMovements++;
      this.behavioralData.mouse_movements = mouseMovements;
    });
  }

  /**
   * Track clicks
   */
  trackClicks() {
    let clicks = 0;

    document.addEventListener('click', () => {
      clicks++;
      this.behavioralData.clicks = clicks;
    });
  }

  /**
   * Track form interactions
   */
  trackFormInteractions() {
    const forms = document.querySelectorAll('form');

    forms.forEach(form => {
      const interactions = [];

      form.addEventListener('focusin', e => {
        if (
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.tagName === 'SELECT'
        ) {
          interactions.push({
            type: 'field_focus',
            field: e.target.name || e.target.id || 'unknown',
            timestamp: Date.now(),
          });
        }
      });

      this.behavioralData.form_interactions = interactions;
    });
  }

  /**
   * Track time on page
   */
  trackTimeOnPage() {
    const startTime = Date.now();

    setInterval(() => {
      this.behavioralData.time_on_page = Math.round(
        (Date.now() - startTime) / 1000
      );
    }, 1000);
  }

  /**
   * Track form interaction
   */
  trackFormInteraction(type, field) {
    const interaction = {
      type: type,
      field: field,
      timestamp: Date.now(),
    };

    this.sessionData.form_interactions.push(interaction);
    sessionStorage.setItem(
      'tracking_session',
      JSON.stringify(this.sessionData)
    );

    // Track form interaction for GTM
    const formType = this.determineFormType(document.querySelector('form'));
    this.trackFormInteractionForGTM(type, field, formType);
  }

  /**
   * Track page interaction
   */
  trackPageInteraction(type) {
    const interaction = {
      type: type,
      timestamp: Date.now(),
    };

    this.userJourney.push(interaction);

    // Track user engagement for GTM
    this.trackUserEngagementForGTM(type, {
      interaction_type: type,
      timestamp: Date.now(),
    });
  }

  /**
   * Track page view
   */
  trackPageView(pageData) {
    this.userJourney.push({
      type: 'page_view',
      ...pageData,
    });
  }

  /**
   * Send behavioral data
   */
  sendBehavioralData() {
    // Send behavioral data to server
    fetch('/analytics/behavioral', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_data: this.sessionData,
        behavioral_data: this.behavioralData,
        user_journey: this.userJourney,
      }),
    }).catch(error => {
      console.error('Behavioral data send error:', error);
    });
  }

  /**
   * Store marketing data
   */
  storeMarketingData() {
    Object.entries(this.marketingData).forEach(([key, value]) => {
      if (value) {
        localStorage.setItem(`marketing_${key}`, value);
      }
    });
  }

  /**
   * Get from storage
   */
  getFromStorage(key) {
    return localStorage.getItem(`marketing_${key}`);
  }

  /**
   * Store conversion data
   */
  storeConversionData(data) {
    localStorage.setItem('last_conversion', JSON.stringify(data));
  }

  /**
   * Generate session ID
   */
  generateSessionId() {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Check if return visitor
   */
  isReturnVisitor() {
    return localStorage.getItem('return_visitor') === 'true';
  }

  /**
   * Get first touch data
   */
  getFirstTouchData() {
    return JSON.parse(localStorage.getItem('first_touch_data') || '{}');
  }

  /**
   * Detect browser
   */
  detectBrowser() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    return 'Unknown';
  }

  /**
   * Detect OS
   */
  detectOS() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  /**
   * Detect device type
   */
  detectDeviceType() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Mobile')) return 'Mobile';
    if (userAgent.includes('Tablet') || userAgent.includes('iPad'))
      return 'Tablet';
    return 'Desktop';
  }

  /**
   * Detect connection type
   */
  detectConnectionType() {
    if ('connection' in navigator) {
      return navigator.connection.effectiveType || 'unknown';
    }
    return 'unknown';
  }

  /**
   * Calculate form completion time
   */
  calculateFormCompletionTime() {
    const formStartTime = this.sessionData.start_time;
    return Date.now() - formStartTime;
  }

  /**
   * Push data to Google Tag Manager DataLayer
   */
  pushToDataLayer(eventName, eventData) {
    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];

    // Push event to dataLayer
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });

    console.log('GTM DataLayer pushed:', eventName, eventData);
  }

  /**
   * Track page view for GTM
   */
  trackPageViewForGTM() {
    this.pushToDataLayer('advanced_page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer,
      marketing_data: this.marketingData,
      device_data: this.deviceData,
      session_data: this.sessionData,
    });
  }

  /**
   * Track scroll depth for GTM
   */
  trackScrollDepthForGTM(scrollPercent) {
    // Track at 25%, 50%, 75%, 90%, 100%
    const milestones = [25, 50, 75, 90, 100];

    if (milestones.includes(scrollPercent)) {
      const eventName = `scroll_depth_${scrollPercent}`;
      this.pushToDataLayer(eventName, {
        scroll_percent: scrollPercent,
        page_title: document.title,
        page_location: window.location.href,
        time_on_page: this.behavioralData.time_on_page,
      });
    }
  }

  /**
   * Track form interaction for GTM
   */
  trackFormInteractionForGTM(interactionType, fieldName, formType) {
    const eventName = `${formType}_${interactionType}`;
    this.pushToDataLayer(eventName, {
      interaction_type: interactionType,
      field_name: fieldName,
      form_type: formType,
      page_title: document.title,
      page_location: window.location.href,
      marketing_data: this.marketingData,
    });
  }

  /**
   * Track user engagement for GTM
   */
  trackUserEngagementForGTM(engagementType, engagementData = {}) {
    const eventName = `user_${engagementType}`;
    this.pushToDataLayer(eventName, {
      engagement_type: engagementType,
      page_title: document.title,
      page_location: window.location.href,
      time_on_page: this.behavioralData.time_on_page,
      scroll_depth: this.behavioralData.scroll_depth,
      session_duration: this.behavioralData.session_duration,
      ...engagementData,
    });
  }

  /**
   * Get form endpoint based on form type
   */
  getFormEndpoint(formType) {
    const endpoints = {
      contact_form: '/contact/submit',
      newsletter: '/newsletter/subscribe',
      quote_request: '/quote/submit',
      callback_request: '/callback/submit',
      service_inquiry: '/service/submit',
    };
    return endpoints[formType] || '/contact/submit';
  }

  /**
   * Submit form with comprehensive data and analytics
   */
  submitFormWithAnalytics(
    form,
    formData,
    formType,
    submitBtn,
    originalBtnContent,
    endpoint
  ) {
    fetch(endpoint, {
      method: 'POST',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.setFormSuccessState(submitBtn);
          this.showSuccessMessage(formType, data.message);

          // Send analytics events only after successful submission
          this.sendToAnalytics(formType, formData);

          // Reset form after success
          setTimeout(() => {
            form.reset();
            this.resetFormState(submitBtn, originalBtnContent);
          }, 2000);
        } else {
          this.setFormErrorState(submitBtn);
          this.showToast('error', data.message || 'An error occurred');

          // Reset after error
          setTimeout(() => {
            this.resetFormState(submitBtn, originalBtnContent);
          }, 3000);
        }
      })
      .catch(() => {
        this.setFormErrorState(submitBtn);
        this.showToast('error', 'Network error occurred');

        // Reset after error
        setTimeout(() => {
          this.resetFormState(submitBtn, originalBtnContent);
        }, 3000);
      });
  }

  /**
   * Submit form with comprehensive data (legacy method)
   */
  submitForm(
    form,
    formData,
    formType,
    submitBtn,
    originalBtnContent,
    endpoint
  ) {
    // This method is now replaced by submitFormWithAnalytics
    this.submitFormWithAnalytics(
      form,
      formData,
      formType,
      submitBtn,
      originalBtnContent,
      endpoint
    );
  }

  /**
   * Show success message based on form type
   */
  showSuccessMessage(formType, message) {
    if (formType === 'newsletter') {
      const currentLang = document.documentElement.lang || 'en';
      const messages = {
        en: 'Successfully subscribed!',
        de: 'Erfolgreich abonniert!',
        fr: 'Abonnement réussi !',
        es: '¡Suscripción exitosa!',
        it: 'Iscrizione completata!',
      };
      this.showToast('success', messages[currentLang] || messages['en']);
    } else {
      this.showToast('success', message || 'Form submitted successfully!');
    }
  }

  /**
   * Show toast notification
   */
  showToast(type, message) {
    if (window.showToast) {
      window.showToast(type, message);
    } else {
      console.log(`Toast [${type}]: ${message}`);
    }
  }

  /**
   * Set form loading state
   */
  setFormLoadingState(isLoading, submitBtn) {
    if (!submitBtn) return;

    if (isLoading) {
      submitBtn.disabled = true;
      submitBtn.classList.add('btn-loading');
      submitBtn.innerHTML =
        '<i class="fas fa-spinner fa-spin me-2"></i>Sending...';
    }
  }

  /**
   * Set form success state
   */
  setFormSuccessState(submitBtn) {
    if (!submitBtn) return;

    submitBtn.classList.remove('btn-loading');
    submitBtn.classList.add('btn-success');
    submitBtn.innerHTML = '<i class="fas fa-check me-2"></i>Message Sent!';
  }

  /**
   * Set form error state
   */
  setFormErrorState(submitBtn) {
    if (!submitBtn) return;

    submitBtn.classList.remove('btn-loading');
    submitBtn.classList.add('btn-error');
    submitBtn.innerHTML =
      '<i class="fas fa-exclamation-triangle me-2"></i>Error - Try Again';
  }

  /**
   * Reset form state
   */
  resetFormState(submitBtn, originalContent) {
    if (!submitBtn) return;

    submitBtn.disabled = false;
    submitBtn.classList.remove('btn-loading', 'btn-success', 'btn-error');
    submitBtn.innerHTML = originalContent;
  }
}

// Initialize advanced tracking when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.advancedTracking = new AdvancedTracking();
});

export default AdvancedTracking;
